/**
 * Contracts for module "cms". Owner: ui-shell.
 * Website CMS run from the staff console (admin host): versioned pages (EN/AR) built from typed sections, a media
 * library (images + video in R2), and collections with categories and items. Published content is served live to
 * the qm-website Worker (service binding) through the public read routes; drafts never leave the admin host.
 *
 * Roles (owner decision 25 Sep 2026): staff_admin (Super Admin) and staff_content (editors) edit and publish pages,
 * items and media; only staff_admin creates or removes collections and categories, and deletes media.
 * Prices are NOT page content: they are catalogue values changed through billing price changes (four-eyes).
 */
import { z } from 'zod';

export const CMS_LOCALES = ['en', 'ar'] as const;
export const CmsLocaleSchema = z.enum(CMS_LOCALES);
export type CmsLocale = z.infer<typeof CmsLocaleSchema>;

/** Site path, same rule as the website generator: lower-case, no trailing slash (except "/"). */
export const CmsPathSchema = z.string().max(200).regex(/^\/([a-z0-9-]+(\/[a-z0-9-]+)*)?$/);
export const CmsSlugSchema = z.string().min(1).max(80).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);
export const CmsIdSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);

/**
 * Section types the website renderer understands. The first group matches qm-website scripts/build-pages.mjs; the
 * CMS adds media and collection sections. Unknown types are refused so a publish can never break the renderer.
 */
export const CMS_SECTION_TYPES = [
  'hero', 'hero-field', 'offer-lockup', 'principles', 'stats', 'channels', 'services', 'how', 'process', 'industry-path',
  'industry-map', 'industry-demo', 'report', 'report-panels', 'scope-split', 'audit-path', 'dashboard', 'clients',
  'pricing', 'plans', 'faq', 'cta', 'buy-bar', 'lead-form',
  // CMS-only sections
  'rich-text', 'image', 'video', 'gallery', 'collection',
] as const;
export const CmsSectionTypeSchema = z.enum(CMS_SECTION_TYPES);

const MAX_TEXT = 20_000;
/** Section field values: text, numbers, booleans, nested objects and lists (depth-limited by size). */
type Json = string | number | boolean | null | Json[] | { [k: string]: Json };
const JsonValue: z.ZodType<Json> = z.lazy(() => z.union([
  z.string().max(MAX_TEXT), z.number().finite(), z.boolean(), z.null(), z.array(JsonValue).max(200), z.record(z.string().max(64), JsonValue),
]));

export const CmsSectionSchema = z.object({
  type: CmsSectionTypeSchema,
  id: CmsSlugSchema.optional(),
}).catchall(JsonValue);
export type CmsSection = z.infer<typeof CmsSectionSchema>;

/** A page document: the same shape as qm-website content/pages/<slug>.json (path/lang/canonical come from the page). */
export const CmsPageDocSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(400).default(''),
  noindex: z.boolean().optional(),
  /** social sharing title/description; omitted = the website keeps the static page's og copy */
  og: z.object({ title: z.string().trim().max(200).optional(), description: z.string().trim().max(400).optional() }).strict().optional(),
  sections: z.array(CmsSectionSchema).min(1).max(60),
}).strict().refine((d) => JSON.stringify(d).length <= 300_000, { message: 'page is too large' });
export type CmsPageDoc = z.infer<typeof CmsPageDocSchema>;

export const CmsPageCreateSchema = z.object({ path: CmsPathSchema, lang: CmsLocaleSchema, doc: CmsPageDocSchema, note: z.string().trim().max(300).optional() }).strict();
/** Save a new draft version. baseVersion = the version the editor started from (409 if someone saved since). */
export const CmsDraftSchema = z.object({ doc: CmsPageDocSchema, baseVersion: z.number().int().min(1), note: z.string().trim().max(300).optional() }).strict();
export const CmsPublishSchema = z.object({ version: z.number().int().min(1) }).strict();

export const CmsPageStatusSchema = z.enum(['draft', 'published', 'unpublished']);
export interface CmsPageView {
  id: string; path: string; lang: CmsLocale; status: z.infer<typeof CmsPageStatusSchema>;
  latestVersion: number; publishedVersion: number | null; publishedAt: string | null; publishedBy: string | null; updatedAt: string;
}
export interface CmsVersionView { version: number; note: string | null; createdBy: string; createdAt: string; doc?: CmsPageDoc }

/** GET /v1/cms/public/pages?path=&lang= (website Worker). */
export interface CmsPublishedPage { path: string; lang: CmsLocale; version: number; publishedAt: string; doc: CmsPageDoc }

// ------------------------------------------------------------------------------------------------ media
export const CMS_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif'] as const;
export const CMS_VIDEO_TYPES = ['video/mp4', 'video/webm'] as const;
/** SVG is refused (it can carry script). */
export const CMS_MEDIA_TYPES = [...CMS_IMAGE_TYPES, ...CMS_VIDEO_TYPES] as const;
export const CMS_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Uploads are buffered in the Worker (sha256 + type sniffing), so video is capped at 50 MB; larger files need a multipart upload (follow-up). */
export const CMS_MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const CmsMediaUpdateSchema = z.object({ altEn: z.string().trim().max(300), altAr: z.string().trim().max(300) }).strict();
export interface CmsMediaView {
  id: string; kind: 'image' | 'video'; mime: string; bytes: number; filename: string; altEn: string; altAr: string;
  url: string; createdAt: string; uploadedBy: string;
}

// ------------------------------------------------------------------------------------------------ collections
export const CmsCollectionCreateSchema = z.object({ key: CmsSlugSchema, nameEn: z.string().trim().min(1).max(120), nameAr: z.string().trim().min(1).max(120) }).strict();
export const CmsCategoryInputSchema = z.object({
  slug: CmsSlugSchema, nameEn: z.string().trim().min(1).max(120), nameAr: z.string().trim().min(1).max(120), sort: z.number().int().min(0).max(10_000).default(0),
}).strict();
export const CmsCategoryReorderSchema = z.object({ ids: z.array(CmsIdSchema).min(1).max(500) }).strict();
export const CmsItemInputSchema = z.object({
  slug: CmsSlugSchema,
  categoryId: CmsIdSchema.nullable().default(null),
  titleEn: z.string().trim().min(1).max(200), titleAr: z.string().trim().min(1).max(200),
  bodyEn: z.string().max(MAX_TEXT).default(''), bodyAr: z.string().max(MAX_TEXT).default(''),
  mediaIds: z.array(CmsIdSchema).max(30).default([]),
  /** free-form values (e.g. price label, link, icon) shown by the collection section */
  fields: z.record(z.string().regex(/^[a-z][a-zA-Z0-9_]{0,39}$/), z.string().max(2000)).default({}),
  sort: z.number().int().min(0).max(10_000).default(0),
  status: z.enum(['draft', 'published']).default('draft'),
}).strict();
export type CmsItemInput = z.infer<typeof CmsItemInputSchema>;

/** GET /v1/cms/public/collections/:key?lang=&category= */
export interface CmsPublicCollection {
  key: string; name: string;
  categories: { id: string; slug: string; name: string; sort: number }[];
  items: { id: string; slug: string; categorySlug: string | null; title: string; body: string; media: { id: string; kind: 'image' | 'video'; url: string; alt: string }[]; fields: Record<string, string>; sort: number }[];
}

/** Public media URL as served through the website (same origin, CSP-friendly). */
export const cmsMediaPath = (id: string) => `/media/${encodeURIComponent(id)}`;
